import { useEffect, useState, useCallback, useContext, createContext } from 'react';
import { Table, Button, Space, Popconfirm, message, Typography, Tag, Spin } from 'antd';
import { PlusOutlined, EditOutlined, DeleteOutlined, ToolOutlined, AppstoreAddOutlined, HolderOutlined } from '@ant-design/icons';
import {
  DndContext, closestCenter, PointerSensor, useSensor, useSensors,
} from '@dnd-kit/core';
import {
  SortableContext, useSortable, verticalListSortingStrategy, arrayMove,
} from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import { api } from '../api';
import CategoryForm from './CategoryForm';

const { Text } = Typography;
const TYPE_COLORS = { component: 'orange', addon: 'blue' };

const RowContext = createContext({});

function DragHandle() {
  const { setActivatorNodeRef, listeners } = useContext(RowContext);
  return (
    <Button
      type="text" size="small"
      icon={<HolderOutlined />}
      style={{ cursor: 'grab', color: '#bbb', touchAction: 'none' }}
      ref={setActivatorNodeRef}
      {...listeners}
    />
  );
}

function DraggableRow({ children, ...props }) {
  const {
    attributes, listeners, setNodeRef, setActivatorNodeRef,
    transform, transition, isDragging,
  } = useSortable({ id: props['data-row-key'] });

  const style = {
    ...props.style,
    transform: CSS.Transform.toString(transform),
    transition,
    ...(isDragging ? { opacity: 0.5, position: 'relative', zIndex: 9999 } : {}),
  };

  return (
    <RowContext.Provider value={{ setActivatorNodeRef, listeners }}>
      <tr {...props} ref={setNodeRef} style={style} {...attributes}>
        {children}
      </tr>
    </RowContext.Provider>
  );
}

export default function CategoriesTab({ storeId, meta }) {
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(false);
  const [saving, setSaving] = useState(false);
  const [editing, setEditing] = useState(null);
  const [expandedRows, setExpandedRows] = useState({});
  const [expandedRowKeys, setExpandedRowKeys] = useState([]);
  const [pageSize, setPageSize] = useState(25);

  const sensors = useSensors(
    useSensor(PointerSensor, { activationConstraint: { distance: 5 } }),
  );

  const load = useCallback(async () => {
    if (!storeId) return;
    setLoading(true);
    try {
      setCategories(await api.categories.list(storeId));
      setExpandedRows({});
      setExpandedRowKeys([]);
    } catch (err) {
      message.error(err.message);
    } finally {
      setLoading(false);
    }
  }, [storeId]);

  useEffect(() => { load(); }, [load]);

  async function handleDelete(id) {
    try {
      await api.categories.delete(id);
      message.success('Gelöscht');
      setCategories(prev => prev.filter(c => c.id !== id));
    } catch (err) {
      message.error(err.message);
    }
  }

  async function openEdit(id) {
    try {
      const cat = await api.categories.get(id);
      setEditing(cat);
    } catch (err) {
      message.error(err.message);
    }
  }

  async function loadSubitems(id) {
    setExpandedRows(prev => ({ ...prev, [id]: { loading: true } }));
    try {
      const full = await api.categories.get(id);
      setExpandedRows(prev => ({
        ...prev,
        [id]: { loading: false, components: full.components || [], addons: full.addons || [] },
      }));
    } catch {
      setExpandedRows(prev => ({ ...prev, [id]: { loading: false, components: [], addons: [] } }));
    }
  }

  function handleExpand(expanded, record) {
    setExpandedRowKeys(prev =>
      expanded ? [...prev, record.id] : prev.filter(k => k !== record.id)
    );
    if (expanded && !expandedRows[record.id]) loadSubitems(record.id);
  }

  async function handleDragEnd({ active, over }) {
    if (!over || active.id === over.id) return;

    const oldIndex = categories.findIndex(c => c.id === active.id);
    const newIndex = categories.findIndex(c => c.id === over.id);
    const reordered = arrayMove(categories, oldIndex, newIndex);
    const withPos = reordered.map((c, i) => ({ ...c, pos: i * 10 }));

    setCategories(withPos);
    setSaving(true);
    try {
      await api.categories.reorder(withPos.map(c => ({ id: c.id, pos: c.pos })));
    } catch (err) {
      message.error('Reihenfolge konnte nicht gespeichert werden: ' + err.message);
      load();
    } finally {
      setSaving(false);
    }
  }

  const subColumns = [
    {
      title: 'Name', dataIndex: 'name',
      render: (name, row) => (
        <Space>
          {row.product_type_name === 'component'
            ? <ToolOutlined style={{ color: '#a07838' }} />
            : <AppstoreAddOutlined style={{ color: '#1677ff' }} />}
          {name || '—'}
        </Space>
      ),
    },
    {
      title: 'Typ', dataIndex: 'product_type_name', width: 110,
      render: name => <Tag color={TYPE_COLORS[name]}>{name}</Tag>,
    },
    {
      title: 'Preis (netto)', dataIndex: 'net_price', width: 130, align: 'right',
      render: v => v != null
        ? <span style={{ fontVariantNumeric: 'tabular-nums' }}>{Number(v).toLocaleString('de-DE', { minimumFractionDigits: 2, maximumFractionDigits: 2 })} €</span>
        : <Text type="secondary">—</Text>,
    },
    {
      title: 'Kaufbar', dataIndex: 'is_purchasable', width: 80,
      render: v => <Tag color={v ? 'green' : 'default'}>{v ? 'Ja' : 'Nein'}</Tag>,
    },
  ];

  const columns = [
    { key: 'sort', width: 40, render: () => <DragHandle /> },
    { title: 'Pos', dataIndex: 'pos', width: 55, align: 'center', render: v => <Text type="secondary" style={{ fontSize: 12 }}>{v}</Text> },
    { title: 'Name', dataIndex: 'name', render: (name, row) => row.is_listed ? <Text strong>{name}</Text> : name },
    { title: 'Produkte', dataIndex: 'product_count', width: 90, align: 'center', render: v => <Text type="secondary">{v ?? 0}</Text> },
    {
      title: '', width: 90,
      render: (_, row) => (
        <Space>
          <Button size="small" icon={<EditOutlined />} onClick={() => openEdit(row.id)} />
          <Popconfirm
            title="Kategorie löschen?"
            okText="Löschen" okButtonProps={{ danger: true }} cancelText="Abbrechen"
            onConfirm={() => handleDelete(row.id)}
          >
            <Button size="small" danger icon={<DeleteOutlined />} />
          </Popconfirm>
        </Space>
      ),
    },
  ];

  return (
    <div>
      <Space style={{ marginBottom: 16 }}>
        <Button type="primary" icon={<PlusOutlined />} onClick={() => setEditing({})}>
          Neue Kategorie
        </Button>
        {saving && <Text type="secondary" style={{ fontSize: 12 }}>Speichern…</Text>}
      </Space>
      <DndContext sensors={sensors} collisionDetection={closestCenter} onDragEnd={handleDragEnd}>
        <SortableContext items={categories.map(c => c.id)} strategy={verticalListSortingStrategy}>
          <Table
            dataSource={categories}
            columns={columns}
            rowKey="id"
            loading={loading}
            size="middle"
            pagination={{ pageSize, showSizeChanger: true, onShowSizeChange: (_, size) => setPageSize(size) }}
            components={{ body: { row: DraggableRow } }}
            expandable={{
              expandedRowKeys,
              onExpand: handleExpand,
              rowExpandable: record => record.component_count > 0 || record.addon_count > 0,
              expandedRowRender: record => {
                const state = expandedRows[record.id];
                if (!state || state.loading) return <Spin size="small" style={{ margin: 12 }} />;
                const hasComponents = state.components?.length > 0;
                const hasAddons = state.addons?.length > 0;
                if (!hasComponents && !hasAddons) {
                  return <Text type="secondary" style={{ padding: '8px 16px', display: 'block' }}>Keine Einträge</Text>;
                }
                return (
                  <div style={{ paddingLeft: 32, paddingBottom: 8 }}>
                    {hasComponents && (
                      <>
                        <Text strong style={{ display: 'block', marginBottom: 6, color: '#a07838' }}>Komponenten</Text>
                        <Table dataSource={state.components} columns={subColumns} rowKey="id"
                          size="small" pagination={false} style={{ marginBottom: hasAddons ? 16 : 0 }} />
                      </>
                    )}
                    {hasAddons && (
                      <>
                        <Text strong style={{ display: 'block', marginBottom: 6, color: '#1677ff' }}>Addons</Text>
                        <Table dataSource={state.addons} columns={subColumns} rowKey="id"
                          size="small" pagination={false} />
                      </>
                    )}
                  </div>
                );
              },
            }}
          />
        </SortableContext>
      </DndContext>
      {editing !== null && (
        <CategoryForm
          category={editing.id ? editing : null}
          storeId={storeId}
          meta={meta}
          onSave={() => { setEditing(null); load(); }}
          onClose={() => setEditing(null)}
        />
      )}
    </div>
  );
}
